/**
 * FirmaTaseronlarPage.jsx — Dekoratör ⇄ Taşeron ilişkisi, firma tarafı.
 * Route: /Firma/Taseronlar
 *
 * Servis: services/firmaTaseronService.js · services/firmaSessionService.js
 * Rehber: docs/guides/FE-FirmaTaseronController-guide.md (yeni controller, 2026-09-03)
 *
 * ─────────────────────────────────────────────────────────────────────────────────────
 * DEĞİŞEN NE: Bir dekoratör bir taşeronu (zemin/temizlik/güvenlik/nakliye) tuttuğunda bu
 * ilişki eskiden `firma_to_stand`'e, talebin açıldığı RASTGELE STANDA yazılıyordu. Yanlış
 * bir cümleydi: dekoratörün zemin firması o dekoratörün kurduğu HER standda çalışır.
 * Artık DEKORATÖR + FUAR'a çapalanıyor (`firma_taseron`) ve stand satırı hiç yazılmıyor.
 * Maiyet fuar başına yeniden pazarlanır — geçen fuarın zemin firması bu fuarınki değildir.
 * ─────────────────────────────────────────────────────────────────────────────────────
 *
 * İKİ SEKME, çünkü BİR FİRMA HER İKİSİ DE OLABİLİR: kendi taşeronlarını tutan bir
 * dekoratör, başka bir dekoratörün taşeronu olarak da gösterilebilir.
 *
 * İKİ KAPI — ve ikisi de gerçekten ayrı bir soru:
 *   Taşeron Firma Beyanı   "Evet, onların taşeronuyuz" + neyi paylaşıyoruz.
 *   Dekoratör Firma Onayı  "Bu firmayı ve personelini onaylıyoruz."
 * Taşeronu talep etmek onay sayılmaz: satın alma kararı, üçüncü bir tarafın işçilerini
 * fuar alanına sokma sorumluluğunu üstlenmekle aynı şey değil (Deniz, 2026-09-03).
 * ⚠️ 2026-09-19'a kadar bu ekran "Kapı 1 / Kapı 2" diyordu; sunucu mesajları artık
 * yukarıdaki iki adı kullanıyor, tek dil için ekran da öyle diyor (backlog #66).
 *
 * DEVİR KALKTI (backlog #381 backend / #382 frontend, 2026-10-13): bağlantı artık yalnızca
 * bir KAYIT + iki taraflı onay. Kâğıt yok (iki kapı da form yüklenmeden yanıtlanır), paylaşım
 * bayrağı yok, "taşeronun personelini gör / adına görevlendir" yok — bir firma başka bir firma
 * adına görevlendirme yapamaz. Ekranda eskiden bunlar için bir Paylaşım sütunu, bir paylaşım
 * penceresi, kapı 1'de iki anahtar ve bir "Personel listesini gör" bağlantısı vardı.
 *
 * İKİ KARAR DA GERİ ALINABİLİR (#67): yanlış verilmiş bir cevap `geri-al` ile Bekliyor'a döner
 * ve eskisi tarihçeye yazılır.
 */
import React, { useCallback, useContext, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Container, Card, CardBody, CardHeader, Row, Col, Button, Badge, Spinner, Alert, Input, Label,
  Table, Modal, ModalHeader, ModalBody, ModalFooter, Nav, NavItem, NavLink, FormGroup,
} from "reactstrap";
import { Handshake, ShieldCheck, ShieldAlert, RefreshCw } from "lucide-react";
import BreadCrumb from "../../Components/Common/BreadCrumb";
import MainContext from "../../Components/Contexts/MainContext";
import {
  getTaseronlarim, getBeniTaseronGosterenler, taseronKarar, dekoratorKarar,
  endTaseronBaglantisi, geriAlTaseronKarar, geriAlDekoratorKarar,
  TASERON_DURUM, DEKORATOR_ONAY, RED_SEBEBI_MAX, KAPI, KAPI_ADI,
} from "../../services/firmaTaseronService";
import { GeriAlModal } from "../../Components/Firma/TaseronKararParcalari";
import { resolveCurrentFirmaId } from "../../services/firmaSessionService";
import { useTranslation } from "react-i18next";
import i18n, { currentLocaleTag } from "../../i18n";

const TABS = [
  { key: "ana", get label() { return i18n.t("portal:taseronlar.taseronlarim"); }, icon: Handshake },
  { key: "taseron", get label() { return i18n.t("portal:taseronlar.beniTaseronGosterenler"); }, icon: ShieldCheck },
];

function formatTarih(value) {
  if (!value) return "—";
  const d = new Date(String(value).replace(" ", "T"));
  return Number.isNaN(d.getTime()) ? String(value) : d.toLocaleString(currentLocaleTag());
}

export default function FirmaTaseronlarPage() {
  const { t: tx } = useTranslation("portal");
  const { selectedFuarId } = useContext(MainContext);
  const fuarId = Number(selectedFuarId) || null;

  const [tab, setTab] = useState("ana");
  const [firmaId, setFirmaId] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [flash, setFlash] = useState(null);
  // Sonlandırılmış bağlantılar varsayılan olarak gizli; `sadeceAktif=false` ile açılır.
  const [gecmisiGoster, setGecmisiGoster] = useState(false);
  const [reloadToken, setReloadToken] = useState(0);

  const [kapi1Modal, setKapi1Modal] = useState(null);
  const [kapi2Modal, setKapi2Modal] = useState(null);
  const [sonlandirModal, setSonlandirModal] = useState(null);
  // { row, kapi } — hangi kapının kararının geri alınacağı.
  const [geriAlModal, setGeriAlModal] = useState(null);

  useEffect(() => { document.title = i18n.t("portal:taseronlar.taseronlarFds"); }, []);

  useEffect(() => {
    let ignore = false;
    resolveCurrentFirmaId().then((id) => { if (!ignore) setFirmaId(id); });
    return () => { ignore = true; };
  }, []);

  // Yeniden okuma sayaçla tetikleniyor: effect gövdesinde doğrudan setState çağırmak
  // react-hooks/set-state-in-effect'i ihlal ediyor (iş iç fonksiyona alınınca etmiyor).
  const reload = useCallback(() => setReloadToken((t) => t + 1), []);

  useEffect(() => {
    let ignore = false;
    async function run() {
      if (!firmaId || !fuarId) { setLoading(false); return; }
      setLoading(true);
      setError(null);
      try {
        const list = tab === "ana"
          ? await getTaseronlarim(firmaId, { fuarId, sadeceAktif: !gecmisiGoster })
          : await getBeniTaseronGosterenler(firmaId, { fuarId, sadeceAktif: !gecmisiGoster });
        if (!ignore) setRows(list);
      } catch (e) {
        if (!ignore) { setError(e.message); setRows([]); }
      } finally {
        if (!ignore) setLoading(false);
      }
    }
    run();
    return () => { ignore = true; };
  }, [firmaId, fuarId, tab, gecmisiGoster, reloadToken]);

  async function run(fn, successText) {
    try {
      await fn();
      setFlash({ color: "success", text: successText });
      setKapi1Modal(null); setKapi2Modal(null); setSonlandirModal(null);
      setGeriAlModal(null);
      reload();
    } catch (e) {
      setFlash({ color: "danger", text: e.message });
    }
  }

  if (!fuarId) {
    return (
      <div className="page-content">
        <Container fluid>
          <BreadCrumb title={tx("taseronlar.taseronlar")} pageTitle={tx("taseronlar.firma")} />
          <Alert fade={false} color="warning">
            {tx("taseronlar.taseronIliskileri")}{" "}<strong>{tx("taseronlar.fuarBasina")}</strong>{" "}{tx("taseronlar.tutulurUsttekiFuarSecicisindenBir")}
          </Alert>
        </Container>
      </div>
    );
  }

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb title={tx("taseronlar.taseronlar")} pageTitle={tx("taseronlar.firma")} />

        {flash && <Alert fade={false} color={flash.color} toggle={() => setFlash(null)}>{flash.text}</Alert>}

        <Nav pills className="mb-3">
          {TABS.map((t) => (
            <NavItem key={t.key}>
              <NavLink
                href="#" className={tab === t.key ? "active" : ""}
                onClick={(e) => { e.preventDefault(); setTab(t.key); setError(null); }}
              >
                <t.icon size={14} className="me-1" />{t.label}
              </NavLink>
            </NavItem>
          ))}
        </Nav>

        <Card>
          <CardHeader className="bg-transparent d-flex align-items-center justify-content-between">
            <span className="fw-semibold">
              {tab === "ana"
                ? tx("taseronlar.fuarIcinTuttugunuzTaseronlar", { fuarId })
                : tx("taseronlar.fuarIcinSiziTaseronGosteren", { fuarId })}
            </span>
            <div className="d-flex align-items-center gap-3">
              <FormGroup switch className="mb-0">
                <Input
                  type="switch" checked={gecmisiGoster} id="gecmis"
                  onChange={(e) => setGecmisiGoster(e.target.checked)}
                />
                <Label check for="gecmis" className="small mb-0">{tx("taseronlar.sonlandirilanlariDaGoster")}</Label>
              </FormGroup>
              <Button size="sm" color="light" onClick={reload} disabled={loading}>
                <RefreshCw size={14} />
              </Button>
            </div>
          </CardHeader>

          <CardBody>
            {error && <Alert fade={false} color="danger">{error}</Alert>}

            {loading ? (
              <div className="text-center py-5"><Spinner /></div>
            ) : rows.length === 0 ? (
              <div className="text-center py-5 text-muted">
                {tab === "ana" ? (
                  <>
                    <Handshake size={32} className="mb-2" />
                    <div>{tx("taseronlar.buFuardaTaseronBaglantinizYok")}</div>
                    <div className="small mt-1" style={{ maxWidth: 640, margin: "0 auto" }}>
                      {tx("taseronlar.taseronTalebiniStandSayfasindanAcarsiniz")}
                      <Link to="/Firma/Standlar">{tx("taseronlar.standlarim")}</Link>{" "}{tx("taseronlar.ilgiliStandStandFirmalariSekmesindeki")}{" "}<em>{tx("taseronlar.firmaEklemeTalebi")}</em>{tx("taseronlar.dekoratorOlarakBir")}
                      <strong>{tx("taseronlar.taseron")}</strong>{" "}{tx("taseronlar.temizlikGuvenlikZeminNakliyeTalep")}{" "}<strong>{tx("taseronlar.sizeVeBuFuara")}</strong>{" "}{tx("taseronlar.baglanir")}
                    </div>
                  </>
                ) : (
                  <>
                    <ShieldCheck size={32} className="mb-2" />
                    <div>{tx("taseronlar.siziTaseronGosterenBirFirma")}</div>
                    <div className="small mt-1">
                      {tx("taseronlar.birDekoratorSiziTaseronuOlarak")}
                    </div>
                  </>
                )}
              </div>
            ) : (
              <div className="table-responsive">
                <Table className="align-middle mb-0">
                  <thead className="table-light">
                    <tr>
                      <th>{tab === "ana" ? tx("taseronlar.taseronFirma") : tx("taseronlar.dekoratorFirma")}</th>
                      <th>{KAPI_ADI[KAPI.TASERON_BEYANI]}</th>
                      <th>{KAPI_ADI[KAPI.DEKORATOR_ONAYI]}</th>
                      <th className="text-end">{tx("taseronlar.islem")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r) => (
                      <TaseronRow
                        key={r.id}
                        row={r}
                        tab={tab}
                        onKapi1={() => setKapi1Modal(r)}
                        onKapi2={() => setKapi2Modal(r)}
                        onGeriAl={(kapi) => setGeriAlModal({ row: r, kapi })}
                        onSonlandir={() => setSonlandirModal(r)}
                      />
                    ))}
                  </tbody>
                </Table>
              </div>
            )}
          </CardBody>
        </Card>

        <Kapi1Modal
          key={`k1-${kapi1Modal?.id ?? "yok"}`}
          row={kapi1Modal}
          onClose={() => setKapi1Modal(null)}
          onSubmit={(payload) => run(
            () => taseronKarar(kapi1Modal.id, payload),
            payload.onayla ? tx("taseronlar.baglantiOnaylandi") : tx("taseronlar.baglantiReddedildi"),
          )}
        />
        <Kapi2Modal
          key={`k2-${kapi2Modal?.id ?? "yok"}`}
          row={kapi2Modal}
          onClose={() => setKapi2Modal(null)}
          onSubmit={(payload) => run(
            () => dekoratorKarar(kapi2Modal.id, payload),
            payload.onayla ? tx("taseronlar.onayinizKaydedildi") : "Onay verilmedi.",
          )}
        />
        <GeriAlModal
          key={`geri-${geriAlModal?.row?.id ?? "yok"}-${geriAlModal?.kapi ?? 0}`}
          row={geriAlModal?.row}
          kapi={geriAlModal?.kapi}
          onClose={() => setGeriAlModal(null)}
          onSubmit={(payload) => run(
            () => (geriAlModal.kapi === KAPI.TASERON_BEYANI
              ? geriAlTaseronKarar(geriAlModal.row.id, payload)
              : geriAlDekoratorKarar(geriAlModal.row.id, payload)),
            tx("taseronlar.geriAlindiKapiYenidenBekliyor", { v: KAPI_ADI[geriAlModal.kapi] }),
          )}
        />

        <SonlandirModal
          row={sonlandirModal}
          tab={tab}
          onClose={() => setSonlandirModal(null)}
          onSubmit={() => run(
            () => endTaseronBaglantisi(sonlandirModal.id),
            tx("taseronlar.baglantiSonlandirildi"),
          )}
        />
      </Container>
    </div>
  );
}

function TaseronRow({ row, tab, onKapi1, onKapi2, onGeriAl, onSonlandir }) {
  const { t } = useTranslation("portal");
  const durumMeta = TASERON_DURUM[row.durum] ?? TASERON_DURUM[0];
  const onayMeta = DEKORATOR_ONAY[row.dekoratorOnay] ?? DEKORATOR_ONAY[0];
  const karsiTaraf = tab === "ana"
    ? { ad: row.taseronFirmaAdi, id: row.taseronFirmaId, tip: row.taseronFirmaTypeAdi }
    : { ad: row.anaFirmaAdi, id: row.anaFirmaId, tip: null };

  // Her kapı bir kez yanıtlanır, AMA yanlış verilmiş cevap artık geri alınabilir (#67):
  // `geri-al` satırı Bekliyor'a döndürür, eskisini tarihçeye yazar. Bu yüzden "yanıtla"
  // ile "geri al" birbirini dışlar — ikisi aynı anda gösterilmez.
  const kapi1Yanitlanabilir = tab === "taseron" && row.aktif && row.durum === 0;
  const kapi1GeriAlinabilir = tab === "taseron" && row.aktif && (row.durum === 1 || row.durum === 2);
  // ⚠️ Kapı 2'nin üzerine doğrudan yazmak fuar sürerken 409 veriyor ve "sürüyor mu"
  // istemcide bilinemiyor (bkz. servis). Bu yüzden yanıtlanmış kapı 2'de TEK yol geri alma.
  const kapi2Yanitlanabilir = tab === "ana" && row.aktif && row.dekoratorOnay === 0;
  const kapi2GeriAlinabilir = tab === "ana" && row.aktif
    && (row.dekoratorOnay === 1 || row.dekoratorOnay === 2);

  return (
    <tr className={row.aktif ? "" : "opacity-50"}>
      <td>
        <div className="fw-medium">
          {karsiTaraf.ad}
          {!row.aktif && <Badge color="secondary" className="ms-2 fw-normal">{t("taseronlar.sonlandirildi")}</Badge>}
        </div>
        <div className="small text-muted">
          {t("taseronlar.firmaId", { id: karsiTaraf.id })}
          {/* `taseronFirmaTypeId` yalnızca ETİKET — hiçbir kural buna dallanmıyor. */}
          {karsiTaraf.tip ? ` · ${karsiTaraf.tip}` : ""}
        </div>
        <div className="small text-muted">{t("taseronlar.talep")}{" "}{formatTarih(row.talepTarihi)}</div>
      </td>

      <td>
        <Badge color={durumMeta.color} className="fw-normal">{row.durumAdi || durumMeta.label}</Badge>
        {row.redSebebi && (
          <div className="small text-danger mt-1" style={{ whiteSpace: "normal", maxWidth: 260 }}>
            {row.redSebebi}
          </div>
        )}
        {row.kararTarihi && <div className="small text-muted mt-1">{formatTarih(row.kararTarihi)}</div>}
      </td>

      <td>
        <Badge color={onayMeta.color} className="fw-normal">{row.dekoratorOnayAdi || onayMeta.label}</Badge>
        {row.dekoratorRedSebebi && (
          <div className="small text-danger mt-1" style={{ whiteSpace: "normal", maxWidth: 260 }}>
            {row.dekoratorRedSebebi}
          </div>
        )}
        {row.dekoratorOnayTarihi && (
          <div className="small text-muted mt-1">{formatTarih(row.dekoratorOnayTarihi)}</div>
        )}
      </td>

      <td className="text-end">
        {kapi1Yanitlanabilir && (
          <Button size="sm" color="primary" className="me-1 mb-1" onClick={onKapi1}>{t("taseronlar.yanitla")}</Button>
        )}
        {kapi1GeriAlinabilir && (
          <Button
            size="sm" color="light" className="me-1 mb-1"
            onClick={() => onGeriAl(KAPI.TASERON_BEYANI)}
          >
            {t("taseronlar.beyaniGeriAl")}
          </Button>
        )}
        {kapi2Yanitlanabilir && (
          <Button size="sm" color="primary" className="me-1 mb-1" onClick={onKapi2}>{t("taseronlar.onayla")}</Button>
        )}
        {kapi2GeriAlinabilir && (
          <Button
            size="sm" color="light" className="me-1 mb-1"
            onClick={() => onGeriAl(KAPI.DEKORATOR_ONAYI)}
          >
            {t("taseronlar.onayiGeriAl")}
          </Button>
        )}
        {row.aktif && (
          <Button size="sm" color="danger" outline className="mb-1" onClick={onSonlandir}>
            {t("taseronlar.sonlandir")}
          </Button>
        )}
      </td>
    </tr>
  );
}

/** KAPI 1 — Taşeron Firma Beyanı: taşeron "evet, onların taşeronuyuz" ya da sebebiyle "hayır" der. */
function Kapi1Modal({ row, onClose, onSubmit }) {
  const { t } = useTranslation("portal");
  const [onayla, setOnayla] = useState(true);
  const [redSebebi, setRedSebebi] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  // Sıfırlama effect'i YOK — modal `key` ile yeniden mount ediliyor, state her açılışta
  // baştan doğuyor.

  if (!row) return null;

  async function submit() {
    if (!onayla && !redSebebi.trim()) {
      setError(t("taseronlar.redSebebiZorunludurBaglantiyiAcan"));
      return;
    }
    setBusy(true); setError(null);
    try {
      await onSubmit(onayla ? { onayla: true } : { onayla: false, redSebebi: redSebebi.trim() });
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal isOpen toggle={() => !busy && onClose()}>
      <ModalHeader toggle={() => !busy && onClose()}>
        {t("taseronlar.siziTaseronuOlarakGosteriyor", { anaFirmaAdi: row.anaFirmaAdi })}
      </ModalHeader>
      <ModalBody>
        {error && <Alert fade={false} color="danger">{error}</Alert>}

        <FormGroup check className="mb-2">
          <Input type="radio" id="k1-evet" checked={onayla} onChange={() => setOnayla(true)} />
          <Label check for="k1-evet">{t("taseronlar.evetBuFirmaninTaseronuyuz")}</Label>
        </FormGroup>
        <FormGroup check className="mb-3">
          <Input type="radio" id="k1-hayir" checked={!onayla} onChange={() => setOnayla(false)} />
          <Label check for="k1-hayir">{t("taseronlar.hayirBuFirmaylaCalismiyoruz")}</Label>
        </FormGroup>

        {!onayla && (
          <>
            <Label className="small">{t("taseronlar.redSebebiEnFazlaKarakter", { RED_SEBEBI_MAX })}</Label>
            <Input
              type="textarea" rows={3} value={redSebebi} maxLength={RED_SEBEBI_MAX}
              onChange={(e) => setRedSebebi(e.target.value)}
              placeholder={t("taseronlar.ornBuFirmaylaBuFuarda")}
            />
            <div className="form-text small">{t("taseronlar.buMetniBaglantiyiAcanFirma")}</div>
          </>
        )}
      </ModalBody>
      <ModalFooter>
        <Button color="light" onClick={onClose} disabled={busy}>{t("taseronlar.vazgec")}</Button>
        <Button color={onayla ? "success" : "danger"} onClick={submit} disabled={busy}>
          {busy ? <Spinner size="sm" /> : onayla ? t("taseronlar.onayla") : t("taseronlar.reddet")}
        </Button>
      </ModalFooter>
    </Modal>
  );
}

/**
 * Dekoratör Firma Onayı — yalnızca HENÜZ YANITLANMAMIŞ satırda açılır. Yanıtlanmış bir
 * onayın üzerine yazmak fuar sürerken 409 verdiği için o satırda "geri al" düğmesi var,
 * bu modal değil (bkz. TaseronRow).
 */
function Kapi2Modal({ row, onClose, onSubmit }) {
  const { t } = useTranslation("portal");
  const [onayla, setOnayla] = useState(true);
  const [redSebebi, setRedSebebi] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  // Sıfırlama effect'i YOK — `key` ile yeniden mount, state prop'tan doğuyor.

  if (!row) return null;

  async function submit() {
    if (!onayla && !redSebebi.trim()) { setError(t("taseronlar.redSebebiZorunludur")); return; }
    setBusy(true); setError(null);
    try {
      await onSubmit(onayla ? { onayla: true } : { onayla: false, redSebebi: redSebebi.trim() });
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal isOpen toggle={() => !busy && onClose()}>
      <ModalHeader toggle={() => !busy && onClose()}>{row.taseronFirmaAdi}{" "}{t("taseronlar.dekoratorFirmaOnayi")}</ModalHeader>
      <ModalBody>
        {error && <Alert fade={false} color="danger">{error}</Alert>}

        <p className="small text-muted">
          {t("taseronlar.buFirmayiSizTalepEttiniz")}{" "}<strong>{t("taseronlar.talepEtmekOnayDegildir")}</strong>{t("taseronlar.buradaBuFirmaylaCalistiginizi")}
        </p>

        <FormGroup check className="mb-2">
          <Input type="radio" id="k2-evet" checked={onayla} onChange={() => setOnayla(true)} />
          <Label check for="k2-evet">{t("taseronlar.buTaseronlaCalistigimizi")}</Label>
        </FormGroup>
        <FormGroup check className="mb-3">
          <Input type="radio" id="k2-hayir" checked={!onayla} onChange={() => setOnayla(false)} />
          <Label check for="k2-hayir">{t("taseronlar.onaylamiyorum")}</Label>
        </FormGroup>

        {!onayla && (
          <>
            <Label className="small">{t("taseronlar.sebepEnFazlaKarakter", { RED_SEBEBI_MAX })}</Label>
            <Input
              type="textarea" rows={3} value={redSebebi} maxLength={RED_SEBEBI_MAX}
              onChange={(e) => setRedSebebi(e.target.value)}
            />
          </>
        )}
      </ModalBody>
      <ModalFooter>
        <Button color="light" onClick={onClose} disabled={busy}>{t("taseronlar.vazgec")}</Button>
        <Button color={onayla ? "success" : "danger"} onClick={submit} disabled={busy}>
          {busy ? <Spinner size="sm" /> : t("common:kaydet")}
        </Button>
      </ModalFooter>
    </Modal>
  );
}

function SonlandirModal({ row, tab, onClose, onSubmit }) {
  const { t } = useTranslation("portal");
  const [busy, setBusy] = useState(false);
  if (!row) return null;
  const karsi = tab === "ana" ? row.taseronFirmaAdi : row.anaFirmaAdi;

  return (
    <Modal isOpen toggle={() => !busy && onClose()}>
      <ModalHeader toggle={() => !busy && onClose()}>{t("taseronlar.baglantiyiSonlandir")}</ModalHeader>
      <ModalBody>
        <p><strong>{karsi}</strong>{" "}{t("taseronlar.ileBuFuardakiTaseronBaglantisi")}</p>
        <Alert fade={false} color="warning" className="small mb-0 d-flex gap-2">
          <ShieldAlert size={18} className="flex-shrink-0 mt-1" />
          <div>
            {t("taseronlar.satirSilinmez")}{" "}<strong>{t("taseronlar.pasifeAlinir")}</strong>{" "}{t("taseronlar.onayliysaAyricaIptalOlur")}
            {tab === "ana" && (
              <div className="mt-1">
                {t("taseronlar.ayniTaseronlaYenidenCalismakIsterseniz")}
              </div>
            )}
          </div>
        </Alert>
      </ModalBody>
      <ModalFooter>
        <Button color="light" onClick={onClose} disabled={busy}>{t("taseronlar.vazgec")}</Button>
        <Button
          color="danger"
          onClick={async () => { setBusy(true); try { await onSubmit(); } finally { setBusy(false); } }}
          disabled={busy}
        >
          {busy ? <Spinner size="sm" /> : t("taseronlar.sonlandir")}
        </Button>
      </ModalFooter>
    </Modal>
  );
}
